import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import './EditionDetailPage.css';

const EditionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [edition, setEdition] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchEditionData = async () => {
      try {
        const { data: editionData, error: editionError } = await supabase
          .from('editions')
          .select('*')
          .eq('id', id)
          .single();

        if (editionError) throw editionError;
        setEdition(editionData);

        // Fetch attendees for this edition
        if (editionData.attendees && editionData.attendees.length > 0) {
          const { data: attendeeData, error: attendeeError } = await supabase
            .from('members')
            .select('*')
            .in('id', editionData.attendees);

          if (attendeeError) throw attendeeError;
          setAttendees(attendeeData || []);
        }
      } catch (error) {
        console.error('Error fetching edition:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEditionData();
  }, [id]);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const handlePreviousImage = () => {
    if (edition?.photos && edition.photos.length > 0) {
      setLightboxIndex((prev) => (prev === 0 ? edition.photos.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (edition?.photos && edition.photos.length > 0) {
      setLightboxIndex((prev) => (prev === edition.photos.length - 1 ? 0 : prev + 1));
    }
  };

  if (loading) {
    return (
      <div className="edition-detail-page">
        <div className="edition-detail-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!edition) {
    return (
      <div className="edition-detail-page">
        <div className="edition-detail-container">
          <button className="back-button" onClick={() => navigate('/members/editions')}>
            ← Retour aux éditions
          </button>
          <p>Édition introuvable</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const photos = edition.photos || [];
  const documents = edition.documents || [];

  return (
    <div className="edition-detail-page">
      <div className="edition-detail-container">
        <button className="back-button" onClick={() => navigate('/members/editions')}>
          ← Retour aux éditions
        </button>

        {/* Header */}
        <div className="edition-detail-header">
          {edition.cover_image && (
            <div className="edition-detail-cover">
              <img src={edition.cover_image} alt={edition.title} />
            </div>
          )}
          <div className="edition-detail-info">
            <div className="edition-detail-date">{formatDate(edition.event_date)}</div>
            <h1>{edition.title}</h1>
            {edition.location && <p className="edition-location">{edition.location}</p>}
          </div>
        </div>

        {/* Description */}
        {edition.description && (
          <div className="edition-description-section">
            <div className="description-content">{edition.description}</div>
          </div>
        )}

        {/* Attendees */}
        {attendees.length > 0 && (
          <section className="attendees-section">
            <h2>Présents ({attendees.length})</h2>
            <div className="attendees-grid">
              {attendees.map((attendee) => (
                <div
                  key={attendee.id}
                  className="attendee-card"
                  onClick={() => setSelectedAttendee(attendee)}
                >
                  {attendee.avatar_url ? (
                    <img src={attendee.avatar_url} alt={attendee.name} className="attendee-avatar" />
                  ) : (
                    <div className="attendee-initials">{getInitials(attendee.name)}</div>
                  )}
                  <div className="attendee-details">
                    <h4>{attendee.name}</h4>
                    {attendee.company && <p>{attendee.company}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Documents */}
        {documents.length > 0 && (
          <section className="documents-section">
            <h2>Documents</h2>
            <div className="documents-list">
              {documents.map((doc, idx) => (
                <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="document-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {doc.name || 'Télécharger le PDF'}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <section className="photos-section">
            <h2>Photos</h2>
            <div className="photos-grid">
              {photos.map((photo, idx) => (
                <div
                  key={idx}
                  className="photo-item"
                  onClick={() => {
                    setLightboxImage(photo.url);
                    setLightboxIndex(idx);
                  }}
                >
                  <img src={photo.url} alt={`Édition ${idx + 1}`} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Attendee Modal */}
      {selectedAttendee && (
        <div className="modal-overlay" onClick={() => setSelectedAttendee(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedAttendee(null)}>×</button>
            <div className="modal-body">
              {selectedAttendee.avatar_url ? (
                <img src={selectedAttendee.avatar_url} alt={selectedAttendee.name} className="modal-avatar" />
              ) : (
                <div className="modal-initials">{getInitials(selectedAttendee.name)}</div>
              )}
              <h2>{selectedAttendee.name}</h2>
              {selectedAttendee.company && <p className="modal-company">{selectedAttendee.company}</p>}
              {selectedAttendee.role && <p className="modal-role">{selectedAttendee.role}</p>}
              {selectedAttendee.bio && <p className="modal-bio">{selectedAttendee.bio}</p>}
              {selectedAttendee.linkedin && (
                <a href={selectedAttendee.linkedin} target="_blank" rel="noopener noreferrer" className="linkedin-button">
                  Voir sur LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
          <button className="lightbox-nav lightbox-prev" onClick={handlePreviousImage}>‹</button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={photos[lightboxIndex].url} alt={`Édition ${lightboxIndex + 1}`} />
            {photos[lightboxIndex].caption && (
              <div className="lightbox-caption">{photos[lightboxIndex].caption}</div>
            )}
          </div>
          <button className="lightbox-nav lightbox-next" onClick={handleNextImage}>›</button>
        </div>
      )}

      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #1f5d5b;
          border-top: 4px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EditionDetailPage;
